package com.iregained.site.components.sections.product.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.product.styles.ElevatedClearanceCardStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Deep Drop-In
@Composable
private fun Modifier.deepDropIn(isVisible: Boolean): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else (-60).px).transition(
            Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut),
            Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut)
        )
}

@Composable
fun ProductClinicalClearanceSection(data: ProductClinicalClearanceSectionData = defaultProductClearanceData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = LocalBreakpoint.current
    val isMobile = breakpoint < Res.Breakpoints.MD
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "product-clinical-clearance-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.description.isNotBlank()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.brand.primary)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }) {
                Box(
                    modifier = ElevatedClearanceCardStyle.toModifier().fillMaxWidth().maxWidth(1000.px)
                        .borderRadius(16.px).padding(topBottom = 40.px, leftRight = if (isMobile) 24.px else 60.px)
                        .deepDropIn(isVisible), contentAlignment = Alignment.Center
                ) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.fillMaxWidth()) {
                        if (data.iconPath.isNotBlank()) Image(
                            src = data.iconPath,
                            modifier = Modifier.unextractable().size(28.px).margin(bottom = 10.px)
                        )
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.margin(bottom = 25.px)
                        ) {
                            if (data.title.isNotBlank()) {
                                SectionHeader(
                                    text = data.title,
                                    fontSize = 24.px,
                                    textColor = Colors.White,
                                    lineColor = Colors.White,
                                    lineWidthPercent = 25.percent,
                                    modifier = Modifier.margin(bottom = 16.px),
                                    alignment = Alignment.CenterHorizontally
                                )
                            }
                        }
                        Div(
                            attrs = Modifier.color(Colors.White).fontSize(if (isMobile) 16.px else 20.px)
                                .fontWeight(FontWeight.Medium).textAlign(TextAlign.Center).lineHeight(1.6).toAttrs {
                                ref { htmlElement ->
                                    htmlElement.innerHTML = data.description; onDispose {}
                                }
                            })
                    }
                }
            }
        }
    }
}

/**
 * CMS IMAGE UPLOAD SPECS:
 * @field iconPath:
 * - Aspect Ratio: 1:1 (Square).
 * - Recommended Dimensions: 32x32px.
 * - Format: SVG (White icon on transparent background).
 */
data class ProductClinicalClearanceSectionData(
    val sectionId: String? = null, val title: String, val description: String, val iconPath: String
)

val defaultProductClearanceData = ProductClinicalClearanceSectionData(
    sectionId = "product-clinical-clearance-section",
    title = "INTENDED USE",
    description = "The IRegained's MyHand<sup>®</sup> System is intended to be used, under the direction of a licensed rehabilitation professional, to assist in the recovery of hand function in patients with hand function disability caused by neurological, neuromuscular, systemic and/or musculoskeletal injuries, disorders, and syndromes.",
    iconPath = Res.Images.ProductPage.Clearance.ICON_INFO
)